import React, { useRef, useEffect, forwardRef } from 'react'
import PropTypes from 'prop-types'
import { getError, errorOptionsType, hintOptionsType } from './zambezi-helpers'
import { classNames, useGlobalId, useUpdateRef, useSetState } from '@zambezi/sdk/common'

import Label from './Label'

/**
 * @type {React.ForwardRefRenderFunction<React.Ref<HTMLInputElement>, import('./interfaces').InputFieldProps>}
 * @param {import('./interfaces').InputFieldProps} props
 * @param {React.Ref<HTMLInputElement>} innerRef
 */
function inputField (props, innerRef) {
  const {
    id: propsId,
    name,
    label,
    defaultValue,
    value,
    type,
    className,
    autoComplete,
    autoFocus,
    error,
    errorOptions,
    errorMessageShown,
    placeholder,
    readOnly,
    renderer,
    hint,
    hintOptions,
    rendererProps,
    disabled,
    required,
    inputProps,
    onChange,
    onBlur,
    onFocus,
    errorInline,
    withAsterisk
  } = props

  const errorId = useGlobalId('zb-formidable-error-')
  const globalId = useGlobalId('zb-control-id-')
  const id = propsId || globalId
  const [{ isFocused, isChanged, value: stateValue }, setState] = useSetState({
    isFocused: false,
    isChanged: false,
    value: value || defaultValue || '',
    error: ''
  })
  const elementRef = useRef()
  useUpdateRef(innerRef, elementRef, { ...props })

  function handleChange ({ target }, val) {
    const value = val !== undefined ? val : target.value
    setState({
      value,
      isChanged: true
    })

    onChange(name, value)
  }

  function handleBlur () {
    setState({
      isFocused: false
    })

    onBlur(name, stateValue)
  }

  function handleFocus () {
    setState({
      isFocused: true,
      isChanged: false
    })

    onFocus(name, stateValue)
  }

  useEffect(
    () => {
      if ('value' in props) setState({ value: value || '' })
    },
    [value]
  )

  function getElement (options) {
    let Renderer = renderer
    const { inputProps, ...rest } = options

    if (Renderer) {
      // only supply inputProps={} when passed and ensure lifecycle
      const extras = inputProps
        ? {
          inputProps: Object.assign({}, inputProps, rest)
        }
        : {}

      // chain options for use in child components
      return <Renderer {...options} {...rendererProps} {...extras} />
    } else {
      Renderer = type === 'textarea' ? 'textarea' : 'input'
      return <Renderer {...inputProps} {...rest} />
    }
  }
  const disabledHandler = disabled ? null : true
  const inputOptions = {
    className: classNames(
      {
        'zb-input': type !== 'textarea',
        'zb-textarea zb-has-scrollbar': type === 'textarea'
      },
      {
        'zb-input-is-error': error && type !== 'textarea',
        'zb-textarea-is-error': error && type === 'textarea',
        'zb-is-error': error
      },
      className
    ),
    type,
    value: stateValue,
    onChange: disabledHandler && handleChange,
    onBlur: disabledHandler && handleBlur,
    onFocus: disabledHandler && handleFocus,
    ref: elementRef
  }

  const labelOptions = {
    inputOptions,
    hint,
    hintOptions,
    label,
    error,
    errorOptions
  }

  const errorMessage = errorMessageShown(error, isFocused, isChanged) || errorInline
    ? getError(error, errorOptions, errorId, errorInline)
    : null

  if (defaultValue !== undefined) {
    inputOptions.defaultValue = defaultValue
    delete inputOptions.value
  }

  if (inputProps) inputOptions.inputProps = inputProps
  if (autoComplete) inputOptions.autoComplete = autoComplete
  if (autoFocus) inputOptions.autoFocus = autoFocus
  if (readOnly) inputOptions.readOnly = readOnly
  if (disabled) inputOptions.disabled = disabled
  if (required) inputOptions.required = required
  if (name) inputOptions.name = name
  if (id) inputOptions.id = id
  if (placeholder) inputOptions.placeholder = placeholder
  if (error) inputOptions['aria-invalid'] = true
  if (errorMessage) inputOptions['aria-describedby'] = errorMessage.props.id

  return (
    <div className='zb-control-wrap'>
      {label ? <Label {...labelOptions}>{label} {required && withAsterisk && <sup className='zb-asterisk'>*</sup>}</Label> : null}
      <div className={classNames('zb-control', { 'zb-is-error': error })}>
        {getElement(inputOptions)}
        {errorMessage}
      </div>
    </div>
  )
}

function noop () {}

const InputField = forwardRef(inputField)

InputField.displayName = 'InputField'

InputField.propTypes = {
  onFocus: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  error: PropTypes.any,
  errorOptions: errorOptionsType,
  errorMessageShown: PropTypes.func,
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.node,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  autoFocus: PropTypes.bool,
  autoComplete: PropTypes.any,
  hint: PropTypes.string,
  hintOptions: hintOptionsType,
  renderer: PropTypes.elementType,
  rendererProps: PropTypes.object,
  inputProps: PropTypes.object,
  errorInline: PropTypes.bool,
  withAsterisk: PropTypes.bool
}

InputField.defaultProps = {
  type: 'text',
  onFocus: noop,
  onChange: noop,
  onBlur: noop,
  errorMessageShown: (error, isFocused, isChanged) =>
    error && isFocused && !isChanged,
  withAsterisk: false
}

export default InputField
